import { useMemo } from 'react';
import { CreditApplication, Supplier, formatMoney } from '@dill/dill-shared';

type ParameterKey =
  | 'yearsInBusiness'
  | 'creditSafeScore'
  | 'creditSafeLimit'
  | 'tradeReferences'
  | 'bankBalance'
  | 'paymentHistory';

const PARAM_LABELS: Record<ParameterKey, string> = {
  yearsInBusiness: 'Years in Business',
  creditSafeScore: 'CreditSafe Score',
  creditSafeLimit: 'CreditSafe Limit',
  tradeReferences: 'Trade References',
  bankBalance: 'Bank Balance (Plaid)',
  paymentHistory: 'Payment History',
};

function formatParamValue(key: ParameterKey, value: number): string {
  if (key === 'bankBalance' || key === 'creditSafeLimit') return formatMoney(value, 0);
  if (key === 'yearsInBusiness') return `${value} yr${value !== 1 ? 's' : ''}`;
  return `${value}`;
}

type Breakpoint = { minValue: string; points: string };

function scoreParam(value: number, breakpoints: Breakpoint[]): number {
  const sorted = [...breakpoints]
    .map((b) => ({ min: parseFloat(b.minValue), pts: parseFloat(b.points) }))
    .filter((b) => !isNaN(b.min) && !isNaN(b.pts))
    .sort((a, b) => a.min - b.min);
  let earned = 0;
  for (const bp of sorted) {
    if (value >= bp.min) earned = bp.pts;
  }
  return earned;
}

const YEAR_COUNT_IDS = ['years-at-present-location', 'company-number-of-years-in-business'];

const YEAR_NUMBER_IDS = [
  'year-established',
  'llc-year-organized',
  'lp-year-organized',
  'government-year-organized',
  'corporation-year-organized',
  'partnership-year-organized',
  'sole-proprietor-year-organized',
];

const DATE_OF_INCORPORATION_IDS = [
  'company-date-of-incorporation',
  'llc-date-of-incorporation',
  'government-date-of-incorporation',
  'corporation-date-of-incorporation',
  'partnership-date-of-incorporation',
  'sole-proprietor-date-of-incorporation',
];

function extractYearsInBusiness(fieldValues: CreditApplication['fieldValues']): number {
  const findById = (id: string) =>
    fieldValues.find((f) => f.id === id || f.id.startsWith(id + '-group-'));

  const currentYear = new Date().getFullYear();

  for (const id of YEAR_COUNT_IDS) {
    const v = parseFloat(String(findById(id)?.value ?? ''));
    if (!isNaN(v) && v > 0) return v;
  }

  for (const id of YEAR_NUMBER_IDS) {
    const v = parseFloat(String(findById(id)?.value ?? ''));
    if (!isNaN(v) && v > 0) {
      return v > 1900 ? Math.max(0, currentYear - v) : v;
    }
  }

  for (const id of DATE_OF_INCORPORATION_IDS) {
    const raw = findById(id)?.value;
    if (raw) {
      const date = new Date(String(raw));
      if (!isNaN(date.getTime())) return Math.max(0, currentYear - date.getFullYear());
    }
  }

  return 0;
}

function extractValues(
  creditApplication: CreditApplication | null | undefined
): Record<ParameterKey, number> {
  const fieldValues = creditApplication?.fieldValues ?? [];

  const findFieldValue = (keyword: string) => {
    const field = fieldValues.find((f) => f.name.toLowerCase().includes(keyword));
    return parseFloat(String(field?.value ?? '0')) || 0;
  };

  const creditSafeReports = creditApplication?.creditSafeReports ?? [];
  const latestCreditSafe = creditSafeReports[0] as any;
  const creditSafeScore =
    parseFloat(
      latestCreditSafe?.data?.report?.companySummary?.creditRating?.providerValue?.value ?? '0'
    ) || 0;
  const creditSafeLimit =
    parseFloat(
      latestCreditSafe?.data?.report?.companySummary?.creditRating?.creditLimit?.value ?? '0'
    ) || 0;

  const plaidReports = creditApplication?.plaidMonthlyReport?.plaidReport ?? [];
  let latestBalance = 0;
  let latestMonth = '';
  for (const report of plaidReports) {
    if (!report?.historicalBalance) continue;
    for (const entry of report.historicalBalance) {
      if (!latestMonth || entry.month > latestMonth) {
        latestMonth = entry.month;
        latestBalance = entry.amount;
      }
    }
  }

  const submittedTradeRefs = (creditApplication?.tradeReferenceHistory ?? []).filter(
    (ref: any) => ref.status === 'Submitted'
  ).length;

  return {
    yearsInBusiness: extractYearsInBusiness(fieldValues),
    creditSafeScore,
    creditSafeLimit,
    tradeReferences: submittedTradeRefs,
    bankBalance: latestBalance,
    paymentHistory: findFieldValue('payment'),
  };
}

function scoreColor(pct: number): string {
  if (pct >= 80) return '#16a34a';
  if (pct >= 55) return '#65a30d';
  if (pct >= 35) return '#ca8a04';
  if (pct >= 15) return '#ea580c';
  return '#dc2626';
}

const ScorecardCard = ({
  supplier,
  creditApplication,
}: {
  supplier?: Supplier | null;
  creditApplication?: CreditApplication | null;
}) => {
  const scorecardSettings = supplier?.creditApplicationSetting?.scorecardSettings;

  const breakdown = useMemo(() => {
    if (!scorecardSettings?.params?.length) return [];
    const rawValues = extractValues(creditApplication);
    return scorecardSettings.params.map((param) => {
      const key = param.key as ParameterKey;
      const rawValue = rawValues[key] ?? 0;
      const max = parseFloat(param.weight) || 0;
      const earned = Math.min(scoreParam(rawValue, param.breakpoints ?? []), max);
      return { key, label: PARAM_LABELS[key] ?? key, earned, max, rawValue };
    });
  }, [scorecardSettings, creditApplication]);

  const total = useMemo(
    () => Math.round(breakdown.reduce((s, b) => s + b.earned, 0) * 10) / 10,
    [breakdown]
  );

  const currentTier = useMemo(() => {
    const tiers = scorecardSettings?.tiers ?? [];
    return (
      [...tiers]
        .map((t) => ({ ...t, min: parseFloat(t.minScore) || 0 }))
        .sort((a, b) => b.min - a.min)
        .find((t) => total >= t.min) ?? null
    );
  }, [scorecardSettings, total]);

  const tierTicks = useMemo(() => {
    const tiers = scorecardSettings?.tiers ?? [];
    const thresholds = tiers.map((t) => parseFloat(t.minScore) || 0);
    return [...new Set([0, ...thresholds, 100])].sort((a, b) => a - b);
  }, [scorecardSettings]);

  const totalColor = scoreColor(total);

  if (!scorecardSettings) return null;

  return (
    <div className="WHITE-BG rounded-lg border border-gray-200 mb-4 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-2.5 border-b border-gray-100 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-wider text-gray-400">
          Dill Scorecard
        </p>
        {currentTier && (
          <span
            className="text-xs font-semibold px-2.5 py-1 rounded-full"
            style={{
              background: totalColor + '15',
              color: totalColor,
              border: `1px solid ${totalColor}30`,
            }}>
            {currentTier.label}
            {currentTier.creditLimit
              ? ` · ${formatMoney(parseFloat(currentTier.creditLimit), 0)}`
              : ''}
          </span>
        )}
      </div>

      {/* Score + bar section */}
      <div className="px-4 pt-3 pb-1">
        {/* Score number row */}
        <div className="flex items-baseline gap-1.5 mb-3">
          <span
            className="text-4xl font-bold tabular-nums leading-none"
            style={{ color: totalColor }}>
            {total}
          </span>
          <span className="text-xs text-gray-400">/ 100</span>
        </div>

        {/* Full-width gradient bar */}
        <div className="relative" style={{ paddingTop: '4px', paddingBottom: '18px' }}>
          {/* Track */}
          <div className="relative rounded-full overflow-hidden" style={{ height: '14px' }}>
            <div
              className="absolute inset-0"
              style={{
                background:
                  'linear-gradient(to right, #ef4444, #f97316, #eab308, #84cc16, #22c55e)',
              }}
            />
            {tierTicks.slice(1, -1).map((tick) => (
              <div
                key={tick}
                className="absolute top-0 bottom-0"
                style={{
                  left: `${tick}%`,
                  width: '1.5px',
                  background: 'rgba(255,255,255,0.4)',
                  transform: 'translateX(-50%)',
                }}
              />
            ))}
          </div>

          {/* Needle */}
          <div
            className="absolute"
            style={{
              left: `clamp(0%, ${total}%, 100%)`,
              top: '1.5px',
              height: '18px',
              width: '3px',
              background: '#0f172a',
              borderRadius: '99px',
              transform: 'translateX(-50%)',
              boxShadow: '0 0 0 2px #fff, 0 1px 4px rgba(0,0,0,0.25)',
              zIndex: 10,
            }}
          />

          {/* Tick labels */}
          <div className="relative" style={{ marginTop: '4px', height: '14px' }}>
            {tierTicks.map((tick) => (
              <span
                key={tick}
                className="absolute text-gray-400"
                style={{ left: `${tick}%`, transform: 'translateX(-50%)', fontSize: '10px' }}>
                {tick}
              </span>
            ))}
          </div>
        </div>
      </div>

      {/* Divider */}
      {breakdown.length > 0 && <div className="border-t border-gray-100 mx-4" />}

      {/* Parameter breakdown */}
      {breakdown.length > 0 && (
        <div className="px-4 py-2.5 grid grid-cols-2 gap-1.5">
          {breakdown.map((b) => {
            const pct = b.max > 0 ? (b.earned / b.max) * 100 : 0;
            const barColor = scoreColor(pct);
            return (
              <div key={b.key} className="rounded-lg border border-gray-100 px-3 py-2 bg-gray-50">
                <p className="text-xs text-gray-400 truncate mb-1">{b.label}</p>
                <p className="text-sm font-bold text-gray-800 leading-snug mb-1.5">
                  {formatParamValue(b.key, b.rawValue)}
                </p>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold" style={{ color: barColor }}>
                    {Math.round(b.earned * 10) / 10}
                    <span className="text-gray-300 font-normal"> / {b.max} pts</span>
                  </span>
                </div>
                <div className="rounded-full bg-gray-200 overflow-hidden" style={{ height: '3px' }}>
                  <div
                    className="h-full rounded-full"
                    style={{ width: `${Math.min(pct, 100)}%`, background: barColor }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ScorecardCard;
